import React, { createContext, useContext, type ReactNode } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { apiRequest } from "@/lib/queryClient";
import { useAuth } from "@/lib/auth-context";

export interface Company {
  id: string;
  name: string;
  code: string | null;
  legalName: string | null;
  displayName: string | null;
  registrationNumber: string | null;
  licenseNumber: string | null;
  taxNumber: string | null;
  companyType: string;
  tenantId: string | null;
  ecosystemScope: string | null;
  contractId: string | null;
  activeWorkers: number;
  complianceScore: number;
  status: string;
  linkedSites: string[];
  contactEmail: string | null;
  contactPhone: string | null;
  contactAddress: string | null;
  contactCity: string | null;
  contactCountry: string | null;
  contactPostalCode: string | null;
  website: string | null;
  approvalStatus: string;
  onboardingStatus: string;
  complianceStatus: string;
  insuranceStatus: string;
  insuranceExpiryDate: string | null;
  isBlacklisted: boolean;
  blacklistReason: string | null;
  maySponsorWorkers: boolean;
  maxWorkersAllowed: number | null;
  defaultCurrency: string | null;
  notes: string | null;
  createdAt: string | null;
  updatedAt: string | null;
  createdBy: string | null;
  updatedBy: string | null;
}

export type CompanyStatus = "active" | "suspended" | "archived" | "pending" | "blocked" | "Active" | "Suspended" | "Archived" | "Pending" | "Blocked";
export type CompanyType = string;
export type CompanyRelationship = any;
export type CompanyContact = any;

interface CompanyContextType {
  companies: Company[];
  setCompanies: (companies: Company[]) => void;
  getCompany: (id: string) => Company | undefined;
  updateCompany: (id: string, updates: Partial<Company>) => void;
  addCompany: (company: Partial<Company>) => void;
  isLoading: boolean;
}

const CompanyContext = createContext<CompanyContextType | null>(null);

export function CompanyProvider({ children }: { children: ReactNode }) {
  const queryClient = useQueryClient();
  const { isAuthenticated } = useAuth();

  const { data: companies = [], isLoading } = useQuery<Company[]>({
    queryKey: ["/api/companies"],
    enabled: isAuthenticated,
  });

  const setCompanies = (_companies: Company[]) => {
    queryClient.setQueryData(["/api/companies"], _companies);
  };

  const getCompany = (id: string) => companies.find((c) => c.id === id);

  const updateCompanyMutation = useMutation({
    mutationFn: async ({ id, updates }: { id: string; updates: Partial<Company> }) => {
      const res = await apiRequest("PATCH", `/api/companies/${id}`, updates);
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/companies"] });
    },
  });

  const addCompanyMutation = useMutation({
    mutationFn: async (company: Partial<Company>) => {
      const res = await apiRequest("POST", "/api/companies", company);
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/companies"] });
    },
  });

  const updateCompany = (id: string, updates: Partial<Company>) => {
    updateCompanyMutation.mutate({ id, updates });
  };

  const addCompany = (company: Partial<Company>) => {
    addCompanyMutation.mutate(company);
  };

  return (
    <CompanyContext.Provider value={{ companies, setCompanies, getCompany, updateCompany, addCompany, isLoading }}>
      {children}
    </CompanyContext.Provider>
  );
}

export function useCompanies() {
  const ctx = useContext(CompanyContext);
  if (!ctx) throw new Error("useCompanies must be used within CompanyProvider");
  return ctx;
}
